/**
 * Tienda e inventario con recetas al estilo de LoL.
 *
 * - Niveles: `basic` (una estadística, baratos) → `epic` (2 básicos + coste de
 *   combinación) → `legendary` (épicos/básicos + combinación, únicos, a veces
 *   con pasivo). `boots`: las botas básicas y sus mejoras (sólo un par).
 * - Precio total = combinación + precio total de cada componente (`totalCost`).
 * - Comprar con componentes en el inventario los consume y descuenta su valor
 *   (también los que están dentro de otros componentes): `purchasePlan`.
 * - Se compra en la base propia o muerto; vender devuelve SHOP.sellRatio del total.
 * - Sprites: `assets/items/<id>.png` (de Super Smash Flash 2, ver LICENSES.md).
 */
import type { HeroId } from './types.ts';

export const SHOP = {
  /** distancia al nexo propio desde la que se puede comprar */
  radius: 11,
  sellRatio: 0.7,
  slots: 6,
};

/** Estadísticas que suma un objeto (mismas claves que HeroStats). */
export interface ItemStats {
  maxHp?: number;
  hpRegen?: number;
  attackDamage?: number;
  /** suma al multiplicador de velocidad de ataque (0.15 = +15%) */
  attackSpeed?: number;
  armor?: number;
  abilityHaste?: number;
  moveSpeed?: number;
  omnivamp?: number;
  lethality?: number;
  armorPen?: number;
  tenacity?: number;
}

export type ItemTier = 'basic' | 'epic' | 'legendary' | 'boots';

/** Roles de objeto (y de build): cada héroe pertenece a uno (`HERO_ITEM_ROLE`). */
export type ItemRole = 'fighter' | 'tank' | 'marksman' | 'assassin';
export const ITEM_ROLES: ItemRole[] = ['fighter', 'tank', 'marksman', 'assassin'];

/** Rol de cada héroe para la tienda (recomendados) y la build de los bots. */
export const HERO_ITEM_ROLE: Record<HeroId, ItemRole> = {
  knight: 'fighter',
  barbarian: 'tank',
  mage: 'marksman',
  falco: 'marksman',
  mario: 'fighter',
  sheik: 'assassin',
};

/**
 * Pasivos únicos (no se acumulan; ver `itemPassive`):
 * - `onHit`: daño plano extra en cada ataque básico.
 * - `thorns`: devuelve esa fracción del daño recibido de héroes/minions.
 * - `takedownHeal`: cura esa fracción de la vida máxima al matar o asistir.
 * - `structureDamage`: +fracción de daño a torres y nexos.
 * - `minionGold`: oro extra al rematar un minion.
 * - `execute`: +fracción de daño a héroes y minions por debajo del 30% de vida.
 * - `minionDamage`: +fracción de daño a minions (limpiar oleadas).
 * - `maxHpOnHit`: los ataques básicos suman esa fracción de la vida máxima del objetivo (no estructuras).
 */
export type ItemPassiveKind =
  | 'onHit'
  | 'thorns'
  | 'takedownHeal'
  | 'structureDamage'
  | 'minionGold'
  | 'execute'
  | 'minionDamage'
  | 'maxHpOnHit';

/** Umbral de vida del pasivo `execute`. */
export const EXECUTE_THRESHOLD = 0.3;

export interface ItemPassive {
  kind: ItemPassiveKind;
  value: number;
  /** nombre del pasivo (español; traducido en el cliente) */
  name: string;
}

export interface ItemDef {
  id: string;
  name: string;
  tier: ItemTier;
  /** roles a los que va orientado (filtro y recomendados de la tienda) */
  roles: ItemRole[];
  /** abreviatura si falta el sprite */
  short: string;
  /** color de acento del icono */
  color: string;
  /** componentes (ids, con repetición) */
  from: string[];
  /** oro extra al combinar (para los básicos, su precio) */
  combine: number;
  /** precio total, calculado de la receta */
  cost: number;
  stats: ItemStats;
  passive?: ItemPassive;
  /** sólo uno en el inventario (todos los legendarios) */
  unique: boolean;
  /** sólo un objeto de este grupo (las botas) */
  group?: string;
  /** objetos en los que se usa (calculado) */
  into: string[];
}

type ItemSeed = Omit<ItemDef, 'cost' | 'into' | 'unique' | 'from'> & { from?: string[]; unique?: boolean };

const seeds: ItemSeed[] = [
  // ------------------------------------------------------------ básicos
  { id: 'fire-flower', name: 'Flor de fuego', tier: 'basic', roles: ['fighter', 'marksman', 'assassin'], short: 'FF', color: '#ff7a3d', combine: 350, stats: { attackDamage: 10 } },
  { id: 'green-shell', name: 'Caparazón verde', tier: 'basic', roles: ['tank', 'fighter'], short: 'CV', color: '#5fbf4a', combine: 300, stats: { armor: 15 } },
  { id: 'super-mushroom', name: 'Superchampiñón', tier: 'basic', roles: ['tank', 'fighter'], short: 'SC', color: '#e8473a', combine: 400, stats: { maxHp: 150 } },
  { id: 'fan', name: 'Abanico', tier: 'basic', roles: ['marksman', 'fighter'], short: 'AB', color: '#f0e3c0', combine: 350, stats: { attackSpeed: 0.15 } },
  { id: 'ice-flower', name: 'Flor de hielo', tier: 'basic', roles: ['fighter', 'assassin', 'tank'], short: 'FH', color: '#7fd4ff', combine: 400, stats: { abilityHaste: 15 } },
  { id: 'food', name: 'Comida', tier: 'basic', roles: ['tank', 'fighter'], short: 'CO', color: '#ffb36b', combine: 300, stats: { hpRegen: 2.5 } },
  { id: 'deku-nut', name: 'Nuez Deku', tier: 'basic', roles: ['tank', 'fighter'], short: 'ND', color: '#c8894a', combine: 300, stats: { tenacity: 0.2 } },
  { id: 'potion', name: 'Poción', tier: 'basic', roles: ['fighter', 'assassin'], short: 'PO', color: '#7fe0d0', combine: 250, stats: { omnivamp: 0.07 } },
  { id: 'capsule', name: 'Cápsula', tier: 'basic', roles: ['assassin', 'fighter'], short: 'CA', color: '#ff9f6b', combine: 300, stats: { lethality: 8 } },
  { id: 'freezie', name: 'Freezie', tier: 'basic', roles: ['marksman', 'assassin', 'fighter'], short: 'FZ', color: '#cfe8ff', combine: 300, stats: { armorPen: 0.12 } },

  // ------------------------------------------------------------ botas (una mejora por rol; ilustraciones propias en SVG)
  { id: 'boots', name: 'Botas', tier: 'boots', roles: ['fighter', 'tank', 'marksman', 'assassin'], group: 'boots', short: 'BO', color: '#c98a52', combine: 300, stats: { moveSpeed: 1 } },
  { id: 'fighter-boots', name: 'Grebas de batalla', tier: 'boots', roles: ['fighter'], group: 'boots', short: 'GB', color: '#ff6b5e', from: ['boots'], combine: 500, stats: { moveSpeed: 1.5, tenacity: 0.2, attackDamage: 6 } },
  { id: 'tank-boots', name: 'Botas blindadas', tier: 'boots', roles: ['tank'], group: 'boots', short: 'BB', color: '#5f8fc8', from: ['boots'], combine: 500, stats: { moveSpeed: 1.5, armor: 20, maxHp: 100 } },
  { id: 'marksman-boots', name: 'Botas aladas', tier: 'boots', roles: ['marksman'], group: 'boots', short: 'BA', color: '#2fae8a', from: ['boots'], combine: 500, stats: { moveSpeed: 1.5, attackSpeed: 0.2 } },
  { id: 'assassin-boots', name: 'Botas sigilosas', tier: 'boots', roles: ['assassin'], group: 'boots', short: 'BS', color: '#6b4fd8', from: ['boots'], combine: 500, stats: { moveSpeed: 1.8, lethality: 8 } },

  // ------------------------------------------------------------ épicos
  // antiguas botas: ahora objetos normales, sin velocidad de movimiento
  { id: 'icewing', name: 'Ala de hielo', tier: 'epic', roles: ['marksman', 'fighter'], short: 'AH', color: '#9fd8ff', from: ['ice-flower', 'fan'], combine: 200, stats: { attackSpeed: 0.15, abilityHaste: 20 } },
  { id: 'screw-attack', name: 'Ataque tornillo', tier: 'epic', roles: ['fighter', 'assassin'], short: 'AT', color: '#ffd166', from: ['fire-flower', 'deku-nut'], combine: 250, stats: { attackDamage: 15, tenacity: 0.15 } },
  { id: 'bumper', name: 'Parachoques', tier: 'epic', roles: ['fighter', 'marksman', 'tank'], short: 'PA', color: '#ff6b6b', from: ['green-shell', 'fan'], combine: 200, stats: { armor: 15, attackSpeed: 0.2 } },
  { id: 'mr-saturn', name: 'Señor Saturno', tier: 'epic', roles: ['fighter', 'tank'], short: 'SS', color: '#ffc9a8', from: ['potion', 'super-mushroom'], combine: 200, stats: { maxHp: 200, omnivamp: 0.08 } },
  { id: 'flipper', name: 'Flipper', tier: 'epic', roles: ['assassin', 'marksman'], short: 'FL', color: '#ff7ab8', from: ['capsule', 'fan'], combine: 200, stats: { lethality: 10, attackSpeed: 0.15 } },
  { id: 'blood-sword', name: 'Espada sangrienta', tier: 'epic', roles: ['fighter', 'assassin'], short: 'ES', color: '#d9364a', from: ['fire-flower', 'food'], combine: 250, stats: { attackDamage: 15, omnivamp: 0.1 } },
  { id: 'poison-dagger', name: 'Daga venenosa', tier: 'epic', roles: ['assassin'], short: 'DV', color: '#7bd36a', from: ['fire-flower'], combine: 300, stats: { attackDamage: 12, lethality: 10 } },
  { id: 'energy-tank', name: 'Tanque de energía', tier: 'epic', roles: ['tank'], short: 'TE', color: '#4f8dff', from: ['super-mushroom', 'food'], combine: 250, stats: { maxHp: 250, hpRegen: 2.5 } },
  { id: 'ore-club', name: 'Garrote de mineral', tier: 'epic', roles: ['fighter', 'marksman'], short: 'GM', color: '#8a7a9a', from: ['fire-flower'], combine: 400, stats: { attackDamage: 15, armorPen: 0.2 } },
  { id: 'red-shell', name: 'Caparazón rojo', tier: 'epic', roles: ['tank'], short: 'CR', color: '#e8473a', from: ['green-shell', 'super-mushroom'], combine: 200, stats: { armor: 20, maxHp: 200 } },
  { id: 'ray-gun', name: 'Pistola de rayos', tier: 'epic', roles: ['marksman'], short: 'PR', color: '#c08cff', from: ['fan', 'fire-flower'], combine: 200, stats: { attackDamage: 12, attackSpeed: 0.2 } },
  { id: 'star-rod', name: 'Vara estelar', tier: 'epic', roles: ['fighter', 'assassin'], short: 'VE', color: '#ffd84d', from: ['fire-flower', 'ice-flower'], combine: 150, stats: { attackDamage: 12, abilityHaste: 18 } },
  { id: 'metal-box', name: 'Caja metálica', tier: 'epic', roles: ['tank'], short: 'CM', color: '#6fd08a', from: ['green-shell', 'green-shell'], combine: 200, stats: { armor: 40 } },
  { id: 'shield-monitor', name: 'Monitor escudo', tier: 'epic', roles: ['tank'], short: 'ME', color: '#5fe08a', from: ['green-shell', 'deku-nut'], combine: 200, stats: { armor: 25, tenacity: 0.15 } },
  {
    id: 'bob-omb', name: 'Bob-omb', tier: 'epic', roles: ['marksman', 'assassin'], short: 'BO', color: '#3a3a3a', from: ['fire-flower', 'freezie'], combine: 250,
    stats: { attackDamage: 12, armorPen: 0.15 }, passive: { kind: 'structureDamage', value: 0.2, name: 'Demolición' },
  },
  {
    id: 'item-bag', name: 'Bolsa de objetos', tier: 'epic', roles: ['tank', 'fighter'], short: 'BS', color: '#f2f2f2', from: ['food'], combine: 150,
    stats: { hpRegen: 1.5 }, passive: { kind: 'minionGold', value: 4, name: 'Botín' },
  },
  { id: 'poison-mushroom', name: 'Champiñón venenoso', tier: 'epic', roles: ['fighter', 'assassin'], short: 'CH', color: '#9a5cc8', from: ['potion', 'fire-flower'], combine: 200, stats: { attackDamage: 12, omnivamp: 0.08 } },
  { id: 'fire-shield-monitor', name: 'Monitor de fuego', tier: 'epic', roles: ['tank', 'fighter'], short: 'MF', color: '#ff8a3d', from: ['super-mushroom', 'ice-flower'], combine: 200, stats: { maxHp: 200, abilityHaste: 18 } },
  { id: 'yellow-shell', name: 'Caparazón amarillo', tier: 'epic', roles: ['tank'], short: 'CAm', color: '#ffd84d', from: ['green-shell', 'food'], combine: 200, stats: { armor: 20, hpRegen: 2.5 } },
  { id: 'motion-sensor-bomb', name: 'Bomba sensor', tier: 'epic', roles: ['assassin'], short: 'BSe', color: '#5fe08a', from: ['capsule', 'fire-flower'], combine: 250, stats: { attackDamage: 15, lethality: 12 } },
  { id: 'firework', name: 'Cohete', tier: 'epic', roles: ['marksman'], short: 'CO', color: '#ff5a5a', from: ['fan', 'freezie'], combine: 200, stats: { attackSpeed: 0.25, armorPen: 0.12 } },
  {
    id: 'gust-jar', name: 'Aspiradora', tier: 'epic', roles: ['fighter', 'marksman', 'tank'], short: 'AS', color: '#8fb8ff', from: ['food', 'fire-flower'], combine: 200,
    stats: { attackDamage: 12, hpRegen: 1.5 }, passive: { kind: 'minionDamage', value: 0.2, name: 'Ráfaga' },
  },
  { id: 'crate', name: 'Caja de madera', tier: 'epic', roles: ['tank', 'fighter'], short: 'CJ', color: '#c8894a', from: ['super-mushroom', 'deku-nut'], combine: 200, stats: { maxHp: 200, tenacity: 0.2 } },
  { id: 'heart-container', name: 'Contenedor de corazón', tier: 'epic', roles: ['tank', 'fighter'], short: 'CC', color: '#ff5a7a', from: ['super-mushroom', 'super-mushroom'], combine: 150, stats: { maxHp: 350 } },

  // ------------------------------------------------------------ legendarios
  {
    id: 'beam-sword', name: 'Espada láser', tier: 'legendary', roles: ['marksman', 'fighter'], short: 'EL', color: '#7dff9a', from: ['ray-gun', 'star-rod'], combine: 700,
    stats: { attackDamage: 35, attackSpeed: 0.25, abilityHaste: 15 }, passive: { kind: 'onHit', value: 12, name: 'Filo de energía' },
  },
  {
    id: 'home-run-bat', name: 'Bate de béisbol', tier: 'legendary', roles: ['fighter', 'marksman'], short: 'BB', color: '#d9d9d9', from: ['ore-club', 'fire-flower', 'fire-flower'], combine: 500,
    stats: { attackDamage: 45, armorPen: 0.25 },
  },
  {
    id: 'medusa-head', name: 'Cabeza de Medusa', tier: 'legendary', roles: ['assassin', 'fighter'], short: 'CMe', color: '#b46cff', from: ['blood-sword', 'poison-dagger'], combine: 600,
    stats: { attackDamage: 35, lethality: 15, omnivamp: 0.12 },
  },
  {
    id: 'explosive-tag', name: 'Sello explosivo', tier: 'legendary', roles: ['assassin'], short: 'SX', color: '#ff8a3d', from: ['poison-dagger', 'star-rod'], combine: 650,
    stats: { attackDamage: 40, lethality: 18, abilityHaste: 15 },
  },
  {
    id: 'spiny-shell', name: 'Caparazón de pinchos', tier: 'legendary', roles: ['tank'], short: 'CP', color: '#4f8dff', from: ['metal-box', 'red-shell'], combine: 700,
    stats: { armor: 70, maxHp: 250, tenacity: 0.15 }, passive: { kind: 'thorns', value: 0.1, name: 'Pinchos' },
  },
  {
    id: 'maxim-tomato', name: 'Tomate Maxim', tier: 'legendary', roles: ['tank', 'fighter'], short: 'TM', color: '#ff4d4d', from: ['heart-container', 'energy-tank'], combine: 650,
    stats: { maxHp: 600, hpRegen: 5, abilityHaste: 10 }, passive: { kind: 'takedownHeal', value: 0.15, name: 'Tomate fresco' },
  },
  {
    id: 'beam-rod', name: 'Vara de rayos', tier: 'legendary', roles: ['fighter'], short: 'VR', color: '#6fd0ff', from: ['star-rod', 'ice-flower', 'super-mushroom'], combine: 600,
    stats: { attackDamage: 30, abilityHaste: 30, maxHp: 200 },
  },
  {
    id: 'smash-ball', name: 'Bola Smash', tier: 'legendary', roles: ['marksman', 'assassin'], short: 'BSm', color: '#ff5ad1', from: ['ray-gun', 'ore-club'], combine: 700,
    stats: { attackDamage: 40, attackSpeed: 0.2, armorPen: 0.15 }, passive: { kind: 'execute', value: 0.2, name: 'Golpe final' },
  },
  {
    id: 'master-ball', name: 'Master Ball', tier: 'legendary', roles: ['fighter', 'assassin'], short: 'MB', color: '#8a5cff', from: ['poison-mushroom', 'star-rod'], combine: 650,
    stats: { attackDamage: 35, omnivamp: 0.15, abilityHaste: 20 },
  },
  {
    id: 'smart-bomb', name: 'Bomba inteligente', tier: 'legendary', roles: ['assassin', 'marksman'], short: 'BI', color: '#ff6b5e', from: ['bob-omb', 'poison-dagger'], combine: 600,
    stats: { attackDamage: 40, lethality: 15, armorPen: 0.2 }, passive: { kind: 'structureDamage', value: 0.35, name: 'Demolición' },
  },
  {
    id: 'lightning-shield-monitor', name: 'Monitor relámpago', tier: 'legendary', roles: ['marksman', 'fighter'], short: 'MR', color: '#4fb8ff', from: ['shield-monitor', 'fan', 'super-mushroom'], combine: 650,
    stats: { armor: 40, attackSpeed: 0.25, maxHp: 250 }, passive: { kind: 'onHit', value: 10, name: 'Descarga' },
  },
  {
    id: 'pow-block', name: 'Bloque POW', tier: 'legendary', roles: ['tank', 'fighter'], short: 'POW', color: '#4f8dff', from: ['heart-container', 'shield-monitor'], combine: 750,
    stats: { maxHp: 450, armor: 35, tenacity: 0.2, abilityHaste: 10 },
  },
  {
    id: 'unira', name: 'Unira', tier: 'legendary', roles: ['tank', 'fighter'], short: 'UN', color: '#5fb8d0', from: ['fire-shield-monitor', 'ice-flower'], combine: 600,
    stats: { maxHp: 300, abilityHaste: 35, hpRegen: 2 },
  },
  {
    id: 'poison-bomb', name: 'Bomba venenosa', tier: 'legendary', roles: ['marksman', 'fighter'], short: 'BV', color: '#9a5cc8', from: ['poison-mushroom', 'firework'], combine: 700,
    stats: { attackDamage: 30, attackSpeed: 0.3, omnivamp: 0.1 }, passive: { kind: 'maxHpOnHit', value: 0.04, name: 'Veneno' },
  },
  {
    id: 'party-ball', name: 'Bola de fiesta', tier: 'legendary', roles: ['tank'], short: 'BF', color: '#ffd166', from: ['item-bag', 'heart-container'], combine: 700,
    stats: { maxHp: 400, hpRegen: 3, abilityHaste: 10 }, passive: { kind: 'minionGold', value: 6, name: 'Fiesta' },
  },
  {
    id: 'assist-trophy', name: 'Trofeo ayudante', tier: 'legendary', roles: ['fighter'], short: 'TA', color: '#9fe8ff', from: ['star-rod', 'fire-shield-monitor'], combine: 650,
    stats: { attackDamage: 30, maxHp: 250, abilityHaste: 25 }, passive: { kind: 'takedownHeal', value: 0.1, name: 'Refuerzos' },
  },
  {
    id: 'bomber', name: 'Bombardero', tier: 'legendary', roles: ['assassin'], short: 'BM', color: '#7a5cff', from: ['motion-sensor-bomb', 'ore-club'], combine: 650,
    stats: { attackDamage: 45, lethality: 18, armorPen: 0.15 }, passive: { kind: 'execute', value: 0.15, name: 'Golpe final' },
  },
  {
    id: 'bubble-shield', name: 'Monitor burbuja', tier: 'legendary', roles: ['tank'], short: 'MBu', color: '#4fd8ff', from: ['yellow-shell', 'crate'], combine: 700,
    stats: { armor: 40, maxHp: 250, tenacity: 0.2, hpRegen: 3 }, passive: { kind: 'thorns', value: 0.08, name: 'Pinchos' },
  },
  {
    id: 'coconut-gun', name: 'Cañón de cocos', tier: 'legendary', roles: ['marksman'], short: 'CC', color: '#b5834a', from: ['firework', 'gust-jar'], combine: 700,
    stats: { attackDamage: 35, attackSpeed: 0.3, hpRegen: 1.5 }, passive: { kind: 'minionDamage', value: 0.35, name: 'Ráfaga' },
  },
  {
    id: 'super-star', name: 'Superestrella', tier: 'legendary', roles: ['tank', 'fighter'], short: 'SE', color: '#ffe066', from: ['shield-monitor', 'energy-tank'], combine: 700,
    stats: { armor: 35, maxHp: 350, tenacity: 0.25, hpRegen: 2 },
  },
];

function buildItems(): Record<string, ItemDef> {
  const items: Record<string, ItemDef> = {};
  for (const seed of seeds) {
    items[seed.id] = { ...seed, from: seed.from ?? [], unique: seed.unique ?? seed.tier === 'legendary', cost: 0, into: [] };
  }
  const total = (id: string): number => {
    const item = items[id];
    if (!item) throw new Error(`items: componente desconocido ${id}`);
    if (item.cost > 0) return item.cost;
    item.cost = item.combine + item.from.reduce((sum, part) => sum + total(part), 0);
    return item.cost;
  };
  for (const id of Object.keys(items)) total(id);
  for (const item of Object.values(items)) {
    for (const part of new Set(item.from)) items[part].into.push(item.id);
  }
  return items;
}

export const ITEMS: Record<string, ItemDef> = buildItems();
export const ITEM_ORDER: string[] = seeds.map((seed) => seed.id);
export const ITEM_TIERS: ItemTier[] = ['basic', 'boots', 'epic', 'legendary'];

export function getItem(id: string): ItemDef | undefined {
  return ITEMS[id];
}

/** Suma de las estadísticas de un inventario. */
export function itemBonuses(items: readonly string[]): Required<ItemStats> {
  const total: Required<ItemStats> = {
    maxHp: 0, hpRegen: 0, attackDamage: 0, attackSpeed: 0, armor: 0, abilityHaste: 0,
    moveSpeed: 0, omnivamp: 0, lethality: 0, armorPen: 0, tenacity: 0,
  };
  for (const id of items) {
    const stats = ITEMS[id]?.stats;
    if (!stats) continue;
    for (const key of Object.keys(stats) as Array<keyof ItemStats>) total[key] += stats[key] ?? 0;
  }
  return total;
}

/** Valor del pasivo `kind` del inventario (los pasivos únicos no se acumulan: el mayor). */
export function itemPassive(items: readonly string[], kind: ItemPassiveKind): number {
  let best = 0;
  for (const id of items) {
    const passive = ITEMS[id]?.passive;
    if (passive?.kind === kind) best = Math.max(best, passive.value);
  }
  return best;
}

export function sellPrice(id: string): number {
  // redondear, no truncar: 350 × 0.7 da 244.999… en coma flotante
  return Math.round((ITEMS[id]?.cost ?? 0) * SHOP.sellRatio);
}

export interface PurchasePlan {
  /** oro a pagar (total menos los componentes que ya tiene) */
  cost: number;
  /** índices del inventario que se consumen */
  consumed: number[];
  /** por qué no se puede (aparte del oro y del lugar): 'unique' | 'group' | 'slots' */
  blocked?: 'unique' | 'group' | 'slots';
}

/**
 * Cuánto cuesta `itemId` con este inventario y qué componentes consume. Recorre
 * la receta: un componente que ya tienes no se paga; si no lo tienes, se miran
 * sus propios componentes. Cada objeto del inventario se usa una sola vez.
 */
export function purchasePlan(items: readonly string[], itemId: string): PurchasePlan | undefined {
  const item = ITEMS[itemId];
  if (!item) return undefined;
  const free = items.map((_, i) => i);
  const consumed: number[] = [];
  const price = (id: string): number => {
    const def = ITEMS[id];
    let cost = def.combine;
    for (const part of def.from) {
      const at = free.findIndex((i) => items[i] === part);
      if (at >= 0) {
        consumed.push(free[at]);
        free.splice(at, 1);
      } else {
        cost += price(part);
      }
    }
    return cost;
  };
  const cost = price(itemId);
  const remaining = free.map((i) => items[i]);
  let blocked: PurchasePlan['blocked'];
  if (item.unique && remaining.includes(itemId)) blocked = 'unique';
  else if (item.group && remaining.some((id) => ITEMS[id]?.group === item.group)) blocked = 'group';
  else if (remaining.length + 1 > SHOP.slots) blocked = 'slots';
  return { cost, consumed: consumed.sort((a, b) => a - b), blocked };
}

/**
 * Objetivos de build de los bots por rol (legendarios y botas). Compran por
 * piezas: si no les llega para el objetivo, el primer componente asequible.
 */
export const BOT_BUILDS: Record<ItemRole, string[]> = {
  fighter: ['fighter-boots', 'beam-rod', 'beam-sword', 'assist-trophy', 'pow-block', 'master-ball'],
  tank: ['tank-boots', 'spiny-shell', 'super-star', 'maxim-tomato', 'pow-block', 'bubble-shield'],
  marksman: ['marksman-boots', 'beam-sword', 'poison-bomb', 'smash-ball', 'coconut-gun', 'lightning-shield-monitor'],
  assassin: ['assassin-boots', 'explosive-tag', 'bomber', 'medusa-head', 'smash-ball', 'smart-bomb'],
};

/**
 * Siguiente compra de un bot hacia `target`: el objetivo entero si le llega el
 * oro; si no, el primer componente (en profundidad) que pueda pagar.
 */
export function nextPurchaseToward(items: readonly string[], gold: number, target: string): string | undefined {
  const plan = purchasePlan(items, target);
  if (!plan || plan.blocked) return undefined;
  if (plan.cost <= gold) return target;
  const owned = [...items];
  for (const part of ITEMS[target].from) {
    const at = owned.indexOf(part);
    if (at >= 0) {
      owned.splice(at, 1);
      continue;
    }
    const next = nextPurchaseToward(items, gold, part);
    if (next) return next;
    return undefined; // ahorra para este componente antes de seguir
  }
  return undefined;
}
